import * as Yup from 'yup';
import { TestaCPF } from '../../utils/getValidationErrors';

/**
 * Interface for attachment type
 */
export interface AttachmentType {
  id: string;
  name: string;
  mandatory: string;
}

/**
 * Interface for file input reference
 */
export interface FileInputRef {
  files: File[] | null;
  setError: (error: string) => void;
}

/**
 * Interface for form data
 */
export interface FormData {
  cityOfBirth?: string;
  informSocialName?: boolean;
  stateOfBirth?: string;
  [key: string]: any;
}

/**
 * Interface for processed file
 */
export interface ProcessedFile {
  name: string;
  file: File;
}

/**
 * Result of file validation
 */
export interface FileValidationResult {
  hasError: boolean;
  errorIds: string[];
}

/**
 * Validates if all mandatory attachments are provided
 * @param isOwner - Whether the form is for owner (titular) or dependent
 * @param attachmentsTypes - List of attachment types with their requirements
 * @param attachmentsRefs - References to file input elements
 * @returns Object with validation result and list of missing attachment IDs
 */
export function validateMandatoryAttachments(
  isOwner: boolean,
  attachmentsTypes: AttachmentType[],
  attachmentsRefs: FileInputRef[],
): FileValidationResult {
  const errorIds: string[] = [];
  let hasError = false;

  if (isOwner) {
    attachmentsTypes.forEach((type) => {
      if (
        type.mandatory === '1' &&
        (!attachmentsRefs[Number(type.id)].files ||
          attachmentsRefs[Number(type.id)].files?.length === 0)
      ) {
        hasError = true;
        errorIds.push(type.id);
      }
    });
  }

  return { hasError, errorIds };
}

/**
 * Processes and formats attachment files for submission
 * @param attachmentsTypes - List of attachment types
 * @param attachmentsRefs - References to file input elements
 * @returns Array of processed files with formatted names
 */
export function processAttachmentFiles(
  attachmentsTypes: AttachmentType[],
  attachmentsRefs: FileInputRef[],
): ProcessedFile[] {
  return attachmentsTypes.reduce((acc, act) => {
    const { files } = attachmentsRefs[Number(act.id)];
    if (files !== null) {
      files?.forEach((f, index) => {
        acc.push({
          name: `${act.name
            .replaceAll('/', ' ')
            .replaceAll(' ', '_')}_${index}.${
            f.name.split('.')[f.name.split('.').length - 1]
          }`,
          file: f,
        });
      });
    }
    return acc;
  }, [] as ProcessedFile[]);
}

/**
 * Adjusts form data by replacing city code with city description
 * @param data - Form data object
 * @returns Adjusted form data with city description
 */
export function adjustFormData(data: FormData): FormData {
  // Get city description from button element if city code is present
  const birthCityId = document.querySelector<HTMLButtonElement>(
    `button[name="cityOfBirth"][value="${data?.cityOfBirth}"]`,
  );

  if (birthCityId && data) {
    const cityDescription = birthCityId.getAttribute('data-description');
    if (cityDescription) {
      return {
        ...data,
        cityOfBirth: cityDescription,
      };
    }
  }

  return data;
}

/**
 * Checks if marital status requires wedding date validation
 * @param maritalStatus - Marital status value from form
 * @returns True if wedding date is required (married or stable union)
 */
export function requiresWeddingDate(
  maritalStatus: string | undefined,
): boolean {
  return maritalStatus === '2' || maritalStatus === '7';
}

/**
 * Checks if at least one contact method (phone or cellphone) is provided
 * @param phone - Phone number value
 * @param cellphone - Cellphone number value
 * @returns True if neither phone nor cellphone is provided
 */
export function requiresContactInfo(
  phone: string | undefined,
  cellphone: string | undefined,
): boolean {
  return !phone && !cellphone;
}

/**
 * Creates Yup validation schema for beneficiary inclusion form
 * @param data - Form data to determine conditional validations
 * @param maritalStatus - Marital status value
 * @param phone - Phone number value
 * @param cellphone - Cellphone number value
 * @returns Yup schema object for form validation
 */
export function createBeneficiaryValidationSchema(
  data: FormData,
  maritalStatus: string | undefined,
  phone: string | undefined,
  cellphone: string | undefined,
): Yup.ObjectSchema<any> {
  const shape: Record<string, Yup.StringSchema | Yup.BooleanSchema> = {};

  // Wedding date is required for married or stable union
  if (requiresWeddingDate(maritalStatus)) {
    shape.weddingDate = Yup.string().required('Informe a data do casamento.');
  }

  // Basic required fields
  shape.beneficiaryName = Yup.string().required('Informe seu nome completo.');
  shape.birthDate = Yup.string().required('Informe sua data de nascimento.');
  shape.hiringDate = Yup.string().required('Informe Data de Adesão.');
  shape.maritalStatus = Yup.string().required('Informe seu estado civil.');
  shape.nationality = Yup.string().required('Informe sua nacionalidade.');
  shape.phisicalSex = Yup.string().required('Informe sua sexualidade.');
  shape.motherName = Yup.string().required('Informe o nome de sua mãe.');

  // Social name is required if informSocialName is true
  if (data?.informSocialName) {
    shape.socialName = Yup.string().required(
      'É necessário informar o nome social',
    );
  }

  // City of birth is required if state of birth is provided
  if (data?.stateOfBirth) {
    shape.cityOfBirth = Yup.string().required('Informe a cidade');
  }

  // CPF validation with custom test
  shape.cpf = Yup.string()
    .required('Informe um CPF Válido.')
    .test('Informe um CPF válido.', 'cpf-test', (value) =>
      TestaCPF(value ?? ''),
    );

  // Address fields
  shape.zipCode = Yup.string().required('Informe seu CEP.');
  shape.state = Yup.string().required('Informe o Estado.');
  shape.city = Yup.string().required('Informe a Cidade.');
  shape.neighborhood = Yup.string().required('Informe o Bairro.');
  shape.placeType = Yup.string().required('Informe o tipo de logradouro.');
  shape.address = Yup.string().required('Informe o Endereço.');
  shape.addressNumber = Yup.string().required('Informe o seu Número.');

  // Contact information
  shape.DDIPhone = Yup.string().required('Informe o DDI do telefone.');
  shape.DDICellphone = Yup.string().required('Informe o DDI do seu celular.');

  // At least one contact method (phone or cellphone) is required
  if (requiresContactInfo(phone, cellphone)) {
    shape.phone = Yup.string().required('Informe um telefone e/ou celular');
    shape.cellphone = Yup.string().required('Informe um telefone e/ou celular');
  }

  // Holder and product information
  shape.holderHiringDate = Yup.string().required(
    'Informe a data de admissão do titular.',
  );
  shape.includeReason = Yup.string().required('Informe o motivo de inclusão.');
  shape.product = Yup.string().required('Informe o produto.');

  // Files validator (will be set to false if files are valid)
  shape.filesValidator = Yup.boolean().isFalse(
    'Necessário informar arquivos obrigatórios',
  );

  return Yup.object().shape(shape);
}

/**
 * Validates beneficiary inclusion form data
 * @param data - Form data to validate
 * @param schema - Yup validation schema
 * @returns Validated data object
 * @throws Yup.ValidationError if validation fails
 */
export async function validateFormData(
  data: FormData,
  schema: Yup.ObjectSchema<any>,
): Promise<FormData> {
  await schema.validate(data, {
    abortEarly: false,
  });
  return data;
}
